import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import { useTheme } from '@mui/material';
import seatInfoFormatter from '../../utils/formatters';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import ListItem from '@mui/material/ListItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import EventSeatIcon from '@mui/icons-material/EventSeat';
import Badge from '@mui/material/Badge';
import Tooltip from '@mui/material/Tooltip';

function MobileDrawer({ seats, allSeats, totalPrice, onRemove, onRemoveAll, onCancel }) {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const theme = useTheme();
  return (
    <Drawer variant="permanent" anchor="bottom">
      <List
        sx={{
          flexGrow: 1,
          mt: 'auto',
          p: 1,
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 1,
        }}
      >
        <ListItem
          sx={(t) => ({
            bgcolor: t.palette.action.hover,
            borderRadius: 1,
            mb: 1,
          })}
        >
          <ListItemText primary="Total:" primaryTypographyProps={{ fontWeight: 600, fontSize: '1rem' }} />
          <ListItemSecondaryAction>
            <Typography variant="subtitle1" fontWeight={700}>
              {`${totalPrice.toFixed(2)} Lei`}
            </Typography>
          </ListItemSecondaryAction>
        </ListItem>
        <ListItem dense sx={{ py: 0.5 }}>
          <ListItemText primary="Nr. Total" />
          <ListItemSecondaryAction>
            <Typography variant="body2">{allSeats.length}</Typography>
          </ListItemSecondaryAction>
        </ListItem>
        <Divider sx={{ my: 1 }} />
        <ListItem sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button onClick={onCancel} variant="contained" sx={{ flexGrow: 1, mr: 1 }}>
            {t('Close')}
          </Button>
          <Tooltip title={t('View selected seats')}>
            <span>
              <IconButton onClick={() => setOpen(true)} disabled={!seats.length}>
                <Badge color="primary" badgeContent={seats.length} invisible={!seats.length}>
                  <EventSeatIcon />
                </Badge>
              </IconButton>
            </span>
          </Tooltip>
        </ListItem>
      </List>
      <Dialog fullScreen={true} open={open}>
        <DialogTitle gutterBottom>
          {t('Selected seats')}
          <IconButton
            onClick={() => setOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 10,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {seats.length ? (
            <List disablePadding>
              {seats.map(
                (
                  {
                    id,
                    singleType,
                    typeId,
                    typeLabel,
                    isDefaultType,
                    section,
                    row,
                    seat,
                    basePrice,
                    discount,
                    price,
                    isFree,
                    categoryLabel,
                    categoryColor,
                  },
                  index
                ) => (
                  <ListItem sx={{ borderLeft: `5px solid ${categoryColor}` }} key={`${id}-${index}`} divider>
                    <ListItemText
                      sx={{ maxWidth: 200 }}
                      primary={
                        <>
                          {categoryLabel}
                          {!isDefaultType && (
                            <Chip sx={{ maxWidth: 100, marginLeft: 1 }} size="small" label={typeLabel} />
                          )}
                        </>
                      }
                      secondary={seatInfoFormatter(section, row, seat, t)}
                    />
                    <ListItemSecondaryAction>
                      <Grid container justifyItems="center" alignItems="center">
                        {isFree || discount ? (
                          <Stack>
                            <span style={{ textDecoration: 'line-through', fontSize: '0.70rem' }}>
                              {(isFree ? price : basePrice).toFixed(2) + ' Lei'}
                            </span>
                            <span style={{ color: theme.palette.error.main }}>
                              {(isFree ? 0 : price).toFixed(2) + ' Lei'}
                            </span>
                          </Stack>
                        ) : (
                          price.toFixed(2) + ' Lei'
                        )}
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() => onRemove(id, singleType ? null : typeId)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </ListItemSecondaryAction>
                  </ListItem>
                )
              )}
              {!!seats.length && (
                <ListItem>
                  <Button fullWidth variant="text" size="small" onClick={onRemoveAll}>
                    {t('Delete')}
                  </Button>
                </ListItem>
              )}
            </List>
          ) : (
            <Box sx={{ height: '100%' }} display="flex" alignItems="center" justifyContent="center">
              <Typography variant="h6">{t('No seat selected')}</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Drawer>
  );
}

export default MobileDrawer;
